<h2 mat-dialog-title>{{ title }}</h2>

<mat-dialog-content>
  @if (isNewCheckInOpen()) {
    <fieldset class="check-in-form">
      <legend class="check-in-form-title">{{ 'workInstruction.checkIn.newCheckIn' | translate }}</legend>
      @if (isMultiple) {
        <mat-dropdown
          class="check-in-work-instruction-dropdown"
          [options]="workInstructionOptions"
          [formControl]="workInstructionControl"
          [configurationInput]="workInstructionDropdownConfiguration"
          [isFormSubmitted]="isCheckInSubmitted"
        ></mat-dropdown>
      }
      @if (stepOptions().length) {
        <div class="check-in-step">
          <mat-dropdown
            class="check-in-step-dropdown"
            [options]="stepOptions()"
            [formControl]="stepControl"
            [configurationInput]="stepDropdownConfiguration"
          ></mat-dropdown>
          <div class="check-in-step-hint">{{ 'workInstruction.checkIn.stepHint' | translate }}</div>
        </div>
      }
      @if (!isUserPickerReady()) {
        <mat-progress-bar class="check-in-users-loading" mode="indeterminate"></mat-progress-bar>
      } @else {
        @if (hasOeeLine()) {
          <app-segmented-button
            class="check-in-user-source"
            size="small"
            [options]="userSourceOptions"
            [value]="userSource()"
            [ariaLabel]="'field.user' | translate"
            (valueChange)="onUserSourceChange($event)"
          ></app-segmented-button>
        }
        <div class="check-in-users">
          <div class="check-in-user-cards" role="group" [attr.aria-label]="'field.user' | translate">
            @for (user of userCards(); track user.id) {
              <button
                type="button"
                class="check-in-user-card"
                [class.is-selected]="user.isSelected"
                [attr.aria-pressed]="user.isSelected"
                (click)="onUserCardClick(user)"
              >
                <app-text-avatar [text]="user.name"></app-text-avatar>
                <span class="check-in-user-card-name">{{ user.name }}</span>
                @if (user.isSelected) {
                  <mat-icon class="check-in-user-card-check">check_circle</mat-icon>
                }
              </button>
            } @empty {
              <span class="check-in-user-cards-empty">{{ 'system.message.noDataAvailable' | translate }}</span>
            }
          </div>
          @if (isCheckInSubmitted && !userControl.value?.length) {
            <span class="check-in-user-error">{{ 'formValidationError.required' | translate }}</span>
          }
        </div>
      }
      <div class="check-in-form-actions">
        <button matButton="tonal" type="button" (click)="onNewCheckInCancel()">
          {{ 'button.cancel' | translate }}
        </button>
        <button mat-flat-button class="check-in-button" type="button" (click)="onCheckInClick()">
          {{ 'button.checkIn' | translate }}
        </button>
      </div>
    </fieldset>
  }

  <div class="check-in-list-header">
    <!-- Same header pattern as "Checked out" below: icon, label, count. -->
    <span class="check-in-section-title">
      <mat-icon>how_to_reg</mat-icon>
      <span>{{ 'workInstruction.checkIn.checkedInList' | translate }}</span>
      <span class="check-in-count">{{ rows().length }}</span>
    </span>
    @if (!isNewCheckInOpen()) {
      <button mat-stroked-button class="check-in-new-button" type="button" (click)="onNewCheckInClick()">
        <mat-icon>person_add</mat-icon>
        {{ 'workInstruction.checkIn.newCheckIn' | translate }}
      </button>
    }
  </div>
  @if (store.isCheckInsLoading()) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  }
  @if (!hasOngoingCheckIns() && !store.isCheckInsLoading()) {
    <div class="check-in-empty">
      <mat-icon>person_off</mat-icon>
      <span>{{ 'workInstruction.checkIn.noCheckIns' | translate }}</span>
    </div>
  }
  @if (rows().length) {
    <div class="check-in-cards">
      @for (row of rows(); track row.checkIn.id) {
        <ng-container [ngTemplateOutlet]="checkInCard" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
      }
    </div>
  }
  @if (checkedOutRows().length) {
    <div class="check-in-section-title check-in-checked-out-header">
      <mat-icon>history</mat-icon>
      <span>{{ 'workInstruction.checkIn.checkedOutList' | translate }}</span>
      <span class="check-in-count">{{ checkedOutRows().length }}</span>
    </div>
    <div class="check-in-cards">
      @for (row of checkedOutRows(); track row.checkIn.id) {
        <ng-container [ngTemplateOutlet]="checkInCard" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
      }
    </div>
  }
  @if (isCheckedOutShown()) {
    @if (store.isCheckedOutCheckInsLoading()) {
      <mat-progress-bar class="check-in-checked-out-loading" mode="indeterminate"></mat-progress-bar>
    } @else if (!hasCheckedOutCheckIns()) {
      <div class="check-in-checked-out-empty">{{ 'workInstruction.checkIn.noCheckedOut' | translate }}</div>
    }
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button
    matButton
    type="button"
    class="check-in-checked-out-toggle"
    [attr.aria-pressed]="isCheckedOutShown()"
    (click)="onToggleCheckedOutClick()"
  >
    <mat-icon>{{ isCheckedOutShown() ? 'visibility_off' : 'history' }}</mat-icon>
    {{
      (isCheckedOutShown() ? 'workInstruction.checkIn.hideCheckedOut' : 'workInstruction.checkIn.showCheckedOut')
        | translate
    }}
  </button>
  <button matButton="tonal" type="button" mat-dialog-close>{{ 'button.close' | translate }}</button>
</mat-dialog-actions>

<ng-template #checkInCard let-row>
  <div class="check-in-card" [class.is-checked-out]="row.checkIn.checkOutTime">
    <app-text-avatar [text]="row.checkIn.user?.name"></app-text-avatar>
    <div class="check-in-card-body">
      <div class="check-in-card-name">{{ row.checkIn.user?.name }}</div>
      <div class="check-in-card-meta">
        <mat-icon>login</mat-icon>
        {{ row.checkIn.checkInTime | utcToUserFormatted: true }}
        @if (row.checkIn.checkOutTime) {
          <mat-icon>logout</mat-icon>
          {{ row.checkIn.checkOutTime | utcToUserFormatted: true }}
        }
      </div>
      <!-- Where the person is checked in: the work instruction, and the step for step check-ins. -->
      <span class="check-in-card-tags">
        <span class="check-in-card-tag is-work-instruction" [matTooltip]="row.workInstructionName">
          <mat-icon>menu_book</mat-icon>
          <span class="check-in-card-tag-text">{{ row.workInstructionName }}</span>
        </span>
        @if (row.stepLabel) {
          <span class="check-in-card-tag is-step" [matTooltip]="row.stepLabel">
            <mat-icon>format_list_numbered</mat-icon>
            <span class="check-in-card-tag-text">{{ row.stepLabel }}</span>
          </span>
        }
      </span>
    </div>
    <span class="check-in-card-duration">
      <mat-icon>schedule</mat-icon>
      {{ row.durationLabel }}
    </span>
    @if (!row.checkIn.checkOutTime) {
      <button
        mat-icon-button
        type="button"
        class="check-in-card-check-out"
        [disabled]="store.isActionLoading()"
        [attr.aria-label]="'button.checkOut' | translate"
        (click)="onCheckOutClick(row.checkIn)"
        matTooltip="{{ 'button.checkOut' | translate }}"
      >
        <mat-icon>logout</mat-icon>
      </button>
    }
  </div>
</ng-template>
